/**
 * Экран выбора игры (главная после входа): карточки режимов во всю ширину — название, подпись
 * «демо», краткое описание и три стола (взнос N); правее — те же режимы на доллары (анонс,
 * неактивны: столы 1 / 10 / 100 $, нажатие показывает «скоро»). Ниже — столы, за которыми сейчас
 * сидят (можно посмотреть). Профиль открывается из верхней панели, проверка файла пакета — в профиле.
 * Карточки строятся по ServerInfo.variants, поэтому новый режим появляется здесь без изменения
 * экрана. Сведения о площадке — только в «Администрировании».
 */
import { useState } from 'react';
import type { GameVariantInfo, RoomSummary, ServerInfo, TableCategory } from '@mt/protocol';
import { hasKey, t, type TKey } from '../i18n';
import { client, useApp } from '../net/client';
import { cr, crn, secondsLeft, useNow } from '../lib/format';
import { isOpenVariant, stageTitle, variantOfCategory, variantText, variantTitle } from '../lib/variants';
import { Rules } from './Rules';
import { VariantArt } from './lobby/VariantArt';

/** Название уровня стола на текущем языке (по взносу: «Начальный», «Стандартный», «Высокий»). */
export function catTitle(cat: { ante: number; title: string }): string {
  const key = `lobby.cat.n${Math.round(cat.ante / 100)}`;
  return hasKey(key) ? t(key as TKey) : cat.title;
}

export function Lobby() {
  const info = useApp((s) => s.info);
  const lobby = useApp((s) => s.lobby);
  const profile = useApp((s) => s.profile);
  const [rules, setRules] = useState(false);
  const now = useNow(500);
  if (!info || !profile) return null;

  const playing = lobby.reduce((a, r) => a + r.seated, 0);
  const active = lobby.filter((r) => r.seated > 0);

  return (
    <div className="lobby">
      <header className="lobby-head">
        <div>
          <h1>{t('lobby.pickTitle')}</h1>
          <p className="muted-line">{t('lobby.pickIntro')}</p>
        </div>
        <dl className="head-stats">
          <div>
            <dt>{t('lobby.balance')}</dt>
            <dd className="mono">{cr(profile.balance)}</dd>
          </div>
          <div>
            <dt>{t('lobby.atTables')}</dt>
            <dd className="mono">{playing}</dd>
          </div>
          <div>
            <dt>{t('lobby.openTables')}</dt>
            <dd className="mono">{lobby.length}</dd>
          </div>
        </dl>
      </header>

      <div className="variants">
        {info.variants.map((v) => (
          <VariantCard
            key={v.id}
            v={v}
            info={info}
            cats={info.categories.filter((c) => c.variantId === v.id)}
            rooms={lobby}
            balance={profile.balance}
            seatedRoomId={profile.seatedRoomId}
          />
        ))}
        {/* те же режимы на доллары — пока только анонс: карточки неактивны, нажатие показывает «скоро» */}
        {info.variants.map((v) => (
          <VariantCard
            key={`soon-${v.id}`}
            v={v}
            info={info}
            cats={info.categories.filter((c) => c.variantId === v.id)}
            rooms={[]}
            balance={profile.balance}
            seatedRoomId={null}
            soon
          />
        ))}
      </div>

      <section className="lobby-live">
        <div className="section-head">
          <h2>{t('lobby.liveTitle')}</h2>
          <button className="btn tiny ghost" onClick={() => setRules(true)}>
            {t('lobby.rulesBtn')}
          </button>
        </div>
        {active.length === 0 ? (
          <p className="muted-line">{t('lobby.liveEmpty')}</p>
        ) : (
          <ul className="room-list panel live-rooms">
            {active.map((r) => (
              <RoomRow key={r.roomId} r={r} info={info} now={now} mine={r.roomId === profile.seatedRoomId} />
            ))}
          </ul>
        )}
      </section>
      {rules && <Rules info={info} onClose={() => setRules(false)} />}
    </div>
  );
}

/** Взносы анонсированных столов на доллары (по уровням столов: начальный, стандартный, высокий). */
const SOON_USD = [1, 10, 100];

/**
 * Карточка режима: иллюстрация, название, описание, особенности и выбор стола.
 * soon — анонс того же режима на доллары: карточка неактивна, столы 1 / 10 / 100 $,
 * нажатие не сажает за стол, а показывает «скоро будут».
 */
function VariantCard(props: { v: GameVariantInfo; info: ServerInfo; cats: TableCategory[]; rooms: RoomSummary[]; balance: number; seatedRoomId: string | null; soon?: boolean }) {
  const { v, cats, rooms, soon = false } = props;
  const title = variantTitle(v);
  const facts = [variantText(v, 'f1', { symbol: v.instrument?.symbol ?? '' }), variantText(v, 'f2')].filter(Boolean) as string[];
  const minAnte = Math.min(...cats.map((c) => c.ante));
  const note = !soon && props.info.mode === 'replay' ? variantText(v, 'demoNote') : null;
  const id = `variant-${v.id}${soon ? '-soon' : ''}`;
  const badge = soon ? t('lobby.soon.label') : variantText(v, 'badge');
  return (
    <article className={`variant v-${v.id} ${isOpenVariant(v) ? 'open' : 'blind'} ${soon ? 'soon' : ''}`} aria-labelledby={id} aria-disabled={soon || undefined}>
      <div className="variant-art">
        <VariantArt id={v.id} symbol={v.instrument?.symbol ?? null} />
        {badge && <span className="variant-badge">{badge}</span>}
      </div>
      <div className="variant-body">
        <header>
          <div>
            <h2 id={id} className="variant-title">
              {title}
            </h2>
            <span className="variant-sub">{soon ? t('lobby.soon.sub') : t('lobby.variant.demo')}</span>
          </div>
          {variantText(v, 'tag') && <span className="variant-tag mono">{variantText(v, 'tag')}</span>}
        </header>
        {variantText(v, 'text') && <p className="variant-text">{variantText(v, 'text')}</p>}
        {facts.length > 0 && (
          <ul className="variant-facts">
            {facts.map((f) => (
              <li key={f}>{f}</li>
            ))}
          </ul>
        )}
        {note && <p className="variant-note">{note}</p>}
        <div className="tiers" role="group" aria-label={t('lobby.chooseTable')}>
          {soon
            ? SOON_USD.map((usd, i) => (
                <button key={usd} className="tier" aria-disabled="true" aria-label={t('lobby.soon.aria', { amount: usd, variant: title })} onClick={() => client.toast('info', t('lobby.soon.toast'))}>
                  <span className="tier-ante mono">
                    {usd}
                    <small> $</small>
                  </span>
                  <span className="tier-name">{cats[i] ? catTitle(cats[i]) : ''}</span>
                  <span className="tier-meta">{t('lobby.soon.tier')}</span>
                </button>
              ))
            : cats.map((cat) => {
                const here = rooms.filter((r) => r.categoryId === cat.id);
                const seated = here.reduce((a, r) => a + r.seated, 0);
                const mine = here.some((r) => r.roomId === props.seatedRoomId);
                return (
                  <button
                    key={cat.id}
                    className={`tier ${mine ? 'mine' : ''}`}
                    disabled={props.balance < cat.ante && !mine}
                    aria-label={t('lobby.tier.aria', { ante: cat.ante, variant: title })}
                    onClick={() => client.join(cat.id)}
                  >
                    <span className="tier-ante mono">
                      {crn(cat.ante)}
                      <small> {t('common.creditsUnit')}</small>
                    </span>
                    <span className="tier-name">{catTitle(cat)}</span>
                    <span className={`tier-meta ${seated ? 'busy' : ''}`}>{mine ? t('lobby.tier.mine') : seated ? t('lobby.tier.seated', { n: seated }) : t('lobby.tier.empty')}</span>
                  </button>
                );
              })}
        </div>
        {!soon && props.balance < minAnte && <p className="notice warn">{t('lobby.notEnough')}</p>}
      </div>
    </article>
  );
}

function RoomRow({ r, info, now, mine }: { r: RoomSummary; info: ServerInfo; now: number; mine: boolean }) {
  const v = variantOfCategory(info, r.categoryId);
  const left = secondsLeft(r.nextStartAt, now);
  const status =
    r.status === 'playing'
      ? r.stage
        ? stageTitle(r.stage, isOpenVariant(v))
        : t('lobby.room.playing')
      : r.status === 'countdown'
        ? t('lobby.room.startIn', { n: left ?? 0 })
        : r.status === 'results'
          ? t('lobby.room.settling')
          : t('lobby.room.ready', { ready: r.ready, min: r.minPlayers });
  return (
    <li className={`room-row ${mine ? 'mine' : ''}`}>
      <span className="room-name">
        <b>{v ? variantTitle(v) : r.categoryId}</b> <span className="mono dim">{cr(r.ante)}</span>
      </span>
      <span className="seat-dots" aria-label={t('lobby.room.taken', { seated: r.seated, max: r.maxPlayers })}>
        {Array.from({ length: r.maxPlayers }, (_, i) => (
          <i key={i} className={i < r.ready ? 'ready' : i < r.seated ? 'taken' : ''} />
        ))}
      </span>
      <span className={`room-status ${r.status}`}>{status}</span>
      <button className="btn tiny ghost" onClick={() => client.watch(r.roomId)}>
        {mine ? t('lobby.room.open') : t('lobby.room.watch')}
      </button>
    </li>
  );
}
